/**
 * La pestaña «Una campaña de Activa» del paso Audiencia: el análisis o la campaña de Nueva campaña, sus grupos (todos o
 * elegidos) y hasta cuántos pueden recibir con la cuenta del chequeo (`useAlcanceOficial`). Las otras pestañas son
 * `AudienciaPublico`, `AudienciaBase` y `AudienciaExcel`.
 */
import { useId } from "react";
import { Link } from "react-router-dom";
import type { AudienciaActiva, CampanaSinWhatsapp, GrupoAudiencia } from "../../types";
import type { EstadoAlcance } from "../../hooks/useAlcanceOficial";
import { optInConCelularDe } from "../../logic/chequeo";
import { etiquetaAnalisis } from "../../logic/formato";
import { rutaResultado } from "../../../segmentaciones/logic/segmentar";
import { AlcanceOficial } from "../AlcanceOficial";
import { AvisoSinWhatsapp } from "./AvisoSinWhatsapp";
import { num, plural } from "../../../campana-nueva/utils/format";

export function PasoAudiencia(p: {
  audiencias: AudienciaActiva[] | null; error: string; audienciaId: string; onAudiencia: (v: string) => void;
  /** las campañas de Activa preparadas con todo por mail (`GET /audiencias/sin-whatsapp`) */
  sinWhatsapp: CampanaSinWhatsapp[];
  modoGrupos: "todos" | "elegir"; onModoGrupos: (v: "todos" | "elegir") => void; elegidos: string[]; onElegidos: (v: string[]) => void;
  /** la cuenta del chequeo para el análisis elegido (`useAlcanceOficial`) */
  alcance: EstadoAlcance; puedenDelGrupo: (g: GrupoAudiencia) => number | null; puedenElegidos: number;
  /** sólo para el equipo: prepara de nuevo la campaña de Nueva campaña (ver `AlcanceOficial`) */
  onPrepararDeNuevo?: (segmentacionId: string) => Promise<string | null>;
}) {
  const idAud = useId();
  const audiencia = p.audiencias?.find((a) => a.id === p.audienciaId);
  const alternar = (g: string) => p.onElegidos(p.elegidos.includes(g) ? p.elegidos.filter((x) => x !== g) : [...p.elegidos, g]);
  const delMotor = (p.audiencias || []).filter((a) => a.motor);
  const deReactivacion = (p.audiencias || []).filter((a) => !a.motor);
  // Con nombre, período y día, todos los del mes se leían igual («Reactivación de octubre de 2026 · 2026-10 ·
  // 8/10/2026»): la hora, los clientes y el comercio los distinguen (`etiquetaAnalisis`).
  const opcion = (a: AudienciaActiva) => (
    <option key={a.id} value={a.id}>
      {etiquetaAnalisis(a)}{a.ofertaElegida === false ? " · falta elegir la oferta" : ""}
    </option>
  );

  if (p.error) return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">No pudimos traer las campañas de Activa. {p.error}</p>;
  if (!p.audiencias) return <div className="h-32 rounded-xl bg-slate-100 animate-pulse" />;
  // hay campañas, pero ninguna con grupos de WhatsApp: se dice cuál y cómo pasarla, no «todavía no hay»
  if (!p.audiencias.length && p.sinWhatsapp.length) return <AvisoSinWhatsapp campanas={p.sinWhatsapp} />;
  if (!p.audiencias.length) {
    return (
      <p className="text-sm text-slate-600">
        Todavía no hay campañas de Activa: prepará una en{" "}
        <Link to="/activa/segmentaciones" className="font-semibold text-violet-600 hover:underline">Nueva campaña</Link>
        {" "}(en Mensajes, «Preparar el envío») o analizá ventas en WhatsApp → QR → Campañas, y vas a poder elegir sus grupos acá.{" "}
        <Link to="/activa/real" className="font-semibold text-violet-600 hover:underline">Analizar ventas</Link>
      </p>
    );
  }
  return (
    <div className="space-y-5">
      <div>
        <label htmlFor={idAud} className="text-xs font-semibold text-slate-600">Campaña de Activa</label>
        <select id={idAud} value={p.audienciaId} onChange={(e) => p.onAudiencia(e.target.value)}
          className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
          <option value="">Elegí una campaña…</option>
          {/* Las del motor (fase 4) primero, separadas de los análisis de Reactivación; sin ninguna, la lista de siempre. */}
          {delMotor.length > 0 ? (
            <>
              <optgroup label="Nueva campaña (con el motor)">{delMotor.map(opcion)}</optgroup>
              {deReactivacion.length > 0 && <optgroup label="Análisis de ventas (Reactivación)">{deReactivacion.map(opcion)}</optgroup>}
            </>
          ) : deReactivacion.map(opcion)}
        </select>
      </div>
      <AvisoSinWhatsapp campanas={p.sinWhatsapp} />
      {audiencia?.motor && (
        <p className="rounded-lg bg-violet-50 px-3 py-2 text-sm text-violet-900" data-testid="audiencia-del-motor">
          {/* El control se mide en el Resultado de Nueva campaña; la medición del oficial no tiene grupo de comparación
              (EmbudoOficial, MedicionBaseVentas): no se promete acá lo que esa pantalla no hace. */}
          Preparada en Nueva campaña: el grupo de control quedó afuera y contra él se mide la campaña en su Resultado de
          Nueva campaña. Acá, en el oficial, esta campaña se mide sin grupo de comparación. Si cambiás un mensaje o una
          oferta, prepará el envío de nuevo.{" "}
          {audiencia.segmentacionId && (
            <span className="inline-flex flex-wrap gap-x-3">
              <Link to={rutaResultado(audiencia.segmentacionId)} className="font-semibold underline whitespace-nowrap">Ver el Resultado</Link>
              <Link to={`/activa/segmentaciones/${encodeURIComponent(audiencia.segmentacionId)}/mensajes`} className="font-semibold underline whitespace-nowrap">Ir a Mensajes</Link>
            </span>
          )}
        </p>
      )}
      {/* Un análisis recién hecho no tiene la oferta elegida: con la «Oferta» en el mensaje, el backend no lo deja
          salir (OFERTA_SIN_ELEGIR). Se avisa acá, al elegirlo, y no recién en Variables o al confirmar. */}
      {audiencia?.ofertaElegida === false && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="oferta-sin-elegir">
          Todavía no elegiste qué ofrecerles a los clientes de este análisis. Si el mensaje lleva la oferta, elegila primero en WhatsApp → QR → Campañas: sin eso no sale.{" "}
          <Link to={`/activa/real?campana=${encodeURIComponent(audiencia.id)}`} className="font-semibold underline whitespace-nowrap">Elegir la oferta</Link>
        </p>
      )}
      {audiencia && (
        <fieldset className="space-y-2">
          <legend className="text-xs font-semibold text-slate-600">Grupos</legend>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="radio" name="grupos" checked={p.modoGrupos === "todos"} onChange={() => p.onModoGrupos("todos")} className="accent-violet-600" /> Todos los grupos
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="radio" name="grupos" checked={p.modoGrupos === "elegir"} onChange={() => p.onModoGrupos("elegir")} className="accent-violet-600" /> Elegir grupos
          </label>
          {p.modoGrupos === "elegir" && (
            <ul className="ml-6 space-y-1.5">
              {audiencia.grupos.map((g) => (
                <li key={g.id}>
                  <label className="flex items-start gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={p.elegidos.includes(g.id)} onChange={() => alternar(g.id)} className="mt-1 accent-violet-600" />
                    <span>
                      {g.emoji} {g.nombre}
                      <span className="block text-xs text-slate-500">
                        {num(g.personas)} {plural(g.personas, "persona", "personas")} · {num(g.conCelular)} con celular · {p.alcance.alcance
                          ? `${num(p.puedenDelGrupo(g) ?? 0)} pueden recibir`
                          : p.alcance.cargando ? "contando…" : `${num(optInConCelularDe(g))} con celular y consentimiento`}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {/* Con la cuenta del chequeo: cuántos, por qué no los demás y qué hacer (lo mismo que vio Reactivación). Si no
              vino, el techo de antes; mientras cuenta, nada que después cambie de número. */}
          {p.alcance.alcance ? (
            <>
              <AlcanceOficial estado={p.alcance} motor={!!audiencia?.motor} onPrepararDeNuevo={p.onPrepararDeNuevo} />
              {p.modoGrupos === "elegir" && p.alcance.alcance.pueden > 0 && (
                <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="alcance-grupos-elegidos">
                  {p.puedenElegidos
                    ? <>Con los grupos que elegiste, hasta <strong>{num(p.puedenElegidos)}</strong> {plural(p.puedenElegidos, "persona", "personas")}.</>
                    : "Ninguno de los grupos que elegiste puede recibir: elegí otros."}
                </p>
              )}
            </>
          ) : p.alcance.cargando ? (
            <AlcanceOficial estado={p.alcance} />
          ) : (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
              Hasta <strong>{num(p.puedenElegidos)}</strong> {plural(p.puedenElegidos, "persona", "personas")} con consentimiento explícito y celular. En WhatsApp oficial sólo se escribe a quien dijo que sí y tiene un celular: los demás quedan afuera.
            </p>
          )}
        </fieldset>
      )}
    </div>
  );
}
